import { useEffect, useRef, useState } from 'react';
import { AuthorizationError, ContainerCheckoutIntent, ContainerExtraSelection, NotFoundError, User } from '@back4app2/sdk';
import back4app2 from '../back4app2';
import { trackEvent } from '../utils/amplitude';
import { Eyebrow } from '../components/ds';
import { CheckIcon, ChevronDownIcon } from '../components/ds/icons';
import { formatMoney } from '../containers/PlanAndExtras';
import usePaddleCheckout, { preloadPaddleScript } from './usePaddleCheckout';
import { rememberStripeCheckout } from './useStripeCheckoutReturn';

/**
 * Buying a container app's plan and extras — the ONLY implementation.
 *
 * There used to be two ways to pay for the same app. The creation wizard used
 * this flow; every other entry point — the "Pay Now" on a pending app, the
 * upgrade links — sent the customer to the pricing site, which runs the legacy
 * checkout and reports back to a webhook host hardcoded to production. An app
 * paid that way stayed "Pending Payment" forever here, and the extras it had
 * chosen were never on the subscription: that path only knows about plans.
 *
 * So the logic lives here, and the screens render it. A second copy is how the
 * two drift again.
 */

/** Paddle's script or frame did not load. The usual cause is named, because
 *  it is the one the buyer can do something about. */
export const CHECKOUT_DID_NOT_LOAD =
  "We couldn't load the checkout. If you use an ad or tracker blocker, allow paddle.com for this page and try again.";

/**
 * `processing`: paid with a method that clears later (boleto, bank debit). The
 * app stays pending and deploys when the webhook says the money landed — the
 * caller must not offer to pay again.
 */
export type PurchaseOutcome =
  | { ok: true; processing?: boolean }
  | { ok: false; reason: 'CLOSED' | 'FAILED'; message?: string };

/** Runs `task` when the browser is idle, so warming a checkout never competes
 *  with the screen that is rendering. */
const whenIdle = (task: () => void) => {
  const idle = (window as unknown as {
    requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => void;
  }).requestIdleCallback;

  if (idle) {
    idle(task, { timeout: 3000 });
  } else {
    window.setTimeout(task, 1500);
  }
};

/** Resolves once `selector` is in the DOM, or after `timeoutMs` regardless —
 *  the caller still checks for the node itself. */
const waitForElement = (selector: string, timeoutMs = 2000) =>
  new Promise<void>((resolve) => {
    const start = performance.now();
    const tick = () => {
      if (document.querySelector(selector) || performance.now() - start > timeoutMs) {
        resolve();
      } else {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);
  });

interface PurchaseArgs {
  appId: string;
  planId: string;
  /** One pack of each — the creation wizard. */
  featureIds?: string[];
  /** Extras with a pack count — Settings, and paying for a pending app. */
  extras?: ContainerExtraSelection[];
  user: User;
  /** The screen the checkout was opened from, on its funnel events. */
  source: string;
  /** What the summary column shows. Money the customer is about to spend has
   *  to be itemised where they are about to spend it. */
  summary: {
    planName: string;
    /**
     * What the plan is, in its own units — "0.5 vCPU · 512 MB · 100 GB
     * transfer" — as one quiet line in the plan's row of the itemisation,
     * so the checkout says what is being bought and not only what it is
     * called. `planCheckoutSpec` writes it.
     */
    planSpec?: string;
    planPrice: number;
    lines: Array<{ id: string; name: string; detail?: string; price: number }>;
    /** Overrides the default "pay for <plan>" label above the plan name. */
    eyebrow?: string;
    /** One sentence under the total, for what this screen needs to add. */
    note?: string;
    /** What the plan gives, one line each, listed above the price lines. */
    features?: string[];
  };
}

/**
 * `lazy`: pass it on a screen that may run another Paddle checkout (Settings,
 * where the legacy plan change initializes its own). Paddle keeps one event
 * callback per page, so this one is taken when the checkout opens, not when
 * the screen renders.
 */
export default function useContainerPurchase(
  options: {
    lazy?: boolean;
    /** A plain dim behind the checkout, without the blur. */
    plainOverlay?: boolean;
  } = {}
) {
  const { configured, openCheckout, closeCheckout } = usePaddleCheckout({
    lazy: options.lazy,
  });
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  // Phone only: whether the line items are open under the price.
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [summary, setSummary] = useState<PurchaseArgs['summary'] | undefined>();
  /**
   * Where this account's purchase most likely goes, asked once when the screen
   * mounts. For Paddle it decides that paddle.js is fetched ahead of the click
   * and that the Paddle customer is looked up alongside the intent. Stripe's
   * checkout is its own page: there is nothing to load for it here. Only a
   * hint: the intent still decides the purchase.
   */
  const providerHintRef = useRef<'PADDLE' | 'STRIPE' | undefined>();

  useEffect(() => {
    let canceled = false;

    whenIdle(() => {
      back4app2
        .containerBillingProvider()
        .then((provider) => {
          if (canceled) {
            return;
          }

          providerHintRef.current = provider;

          if (provider === 'PADDLE') {
            preloadPaddleScript();
          }
        })
        // An older server, or no session: Paddle is still the default.
        .catch(() => {
          if (!canceled) {
            preloadPaddleScript();
          }
        });
    });

    return () => { canceled = true; };
  }, []);

  // Who is waiting on the checkout. Paddle's `checkout.closed` does not arrive
  // when WE tear the frame down, so our own ✕ has to settle the wait itself.
  const resolveRef = useRef<((outcome: PurchaseOutcome) => void) | null>(null);

  const settle = (outcome: PurchaseOutcome) => {
    const resolve = resolveRef.current;

    resolveRef.current = null;
    setIsCheckoutOpen(false);
    resolve?.(outcome);
  };

  const purchase = async (args: PurchaseArgs): Promise<PurchaseOutcome> => {
    const { appId, planId, featureIds, extras, user } = args;
    let intent: ContainerCheckoutIntent;

    // An account headed for Paddle needs its Paddle customer too, and the two
    // calls do not depend on each other: asking both at once saves a round
    // trip, the slower one since it reaches Paddle's API. Never for an account
    // headed for Stripe — the lookup CREATES a Paddle customer.
    const customerLookup =
      providerHintRef.current === 'PADDLE' ? lookUpPaddleCustomer() : undefined;

    try {
      intent = await back4app2.containerCheckoutIntent(planId, featureIds, extras);
    } catch (e) {
      console.error('could not resolve the checkout intent', e);

      return {
        ok: false,
        reason: 'FAILED',
        message: "We couldn't reach the payment service.",
      };
    }

    if (intent.provider === 'STRIPE') {
      return await purchaseWithStripe(args);
    }

    // Always a checkout: the subscription belongs to the app, a new or pending
    // app has none, and Paddle creates subscriptions from checkouts only.
    if (!configured) {
      console.error('checkout requested but Paddle is not configured');

      return {
        ok: false,
        reason: 'FAILED',
        message: 'Checkout is unavailable right now.',
      };
    }

    const customerId = await (customerLookup || lookUpPaddleCustomer());

    return await new Promise<PurchaseOutcome>((resolve) => {
      resolveRef.current = resolve;
      setSummary(args.summary);
      setIsDetailsOpen(false);
      setIsCheckoutOpen(true);

      // Paddle draws into a node that has to exist already, and the state
      // change above is what creates it — so the frame opens as soon as it
      // is there, rather than after a fixed pause.
      waitForElement('.checkout-container').then(() => {
        openCheckout({
          // Lines with their quantity: two packs of one extra are ONE Paddle
          // item with quantity 2, never the same price twice.
          items: intent.items,
          frameTarget: 'checkout-container',
          customerId: customerId || intent.customerId || undefined,
          customerEmail: user.username,
          // Only what something on the other side reads: appId and planId for
          // the checkout app's record, userId for the ownership check.
          customData: { appId, userId: user.id },
          onCompleted: (transactionId, checkoutId) => {
            back4app2
              .confirmContainerCheckout(appId, planId, featureIds, transactionId, checkoutId)
              // The card was charged either way. `false` only means Paddle had
              // not materialised the subscription yet — offering "continue on
              // Free" to someone who just paid would be the worst answer.
              .then(() => settle({ ok: true }))
              .catch((e) => {
                // A refusal is not a hiccup. The server turns the payment away
                // when the buyer does not own the app — and so does the
                // webhook, so nothing is coming to apply it later. Reporting
                // that as "Payment received" told someone who had been charged
                // for nothing that all was well. Anything else (the network, a
                // timeout) the webhook still converges, and stays a success.
                if (e instanceof AuthorizationError || e instanceof NotFoundError) {
                  console.error('the checkout was paid but refused for this app', e);
                  settle({
                    ok: false,
                    reason: 'FAILED',
                    message: "The payment went through, but it couldn't be applied to this app. Contact support and we'll sort it out.",
                  });

                  return;
                }

                settle({ ok: true });
              });
          },
          onClosed: () => settle({ ok: false, reason: 'CLOSED' }),
          tracking: { source: args.source, app_id: appId, plan_id: planId },
          // Express everywhere but the Plan screen, where picking a size in
          // the table is the decision and the one-page form follows it.
          variant: args.source === 'plan_settings' ? 'one-page' : 'express',
        }).then((opened) => {
          // Our modal is already up, waiting for Paddle to draw into it. If
          // Paddle never loaded there is nothing coming: close it and say so,
          // rather than leave an empty panel with a ✕.
          if (!opened) {
            settle({ ok: false, reason: 'FAILED', message: CHECKOUT_DID_NOT_LOAD });
          }
        });
      });
    });
  };

  /** The account's Paddle customer, or none when the lookup fails — the
   *  checkout then falls back to the e-mail, as it always has. */
  const lookUpPaddleCustomer = async (): Promise<string | undefined> => {
    try {
      // Locks the email on the checkout: one back4app account, one Paddle
      // customer. It CREATES a customer, so it runs only for a Paddle purchase.
      return await back4app2.ensureContainersPaddleCustomer();
    } catch (e) {
      console.error('could not resolve the Paddle customer', e);

      return undefined;
    }
  };

  /**
   * The same purchase on Stripe's own checkout page. The server opens the
   * session — lines, customer, the app in its metadata — and the browser goes
   * there; it comes back to the app's page, where `useStripeCheckoutReturn`
   * confirms the payment. A buyer who gives up there comes back to an app that
   * waits for its payment, deploying nothing until then.
   *
   * Resolves only when the session could not be opened. Otherwise the page is
   * leaving, and no caller may act on an outcome — closing a checkout deletes
   * a new app, which is not what leaving for Stripe means.
   */
  const purchaseWithStripe = async (args: PurchaseArgs): Promise<PurchaseOutcome> => {
    const { appId, planId, featureIds, extras } = args;
    // Same funnel names and tags as the Paddle checkout, so one chart reads both;
    // `variant` splits them as it already splits Paddle's two layouts.
    const tags = {
      source: args.source,
      app_id: appId,
      plan_id: planId,
      provider: 'stripe',
      variant: 'stripe_hosted',
    };

    let session: { url: string; sessionId: string };

    try {
      session = await back4app2.createContainerStripeCheckout(
        appId,
        planId,
        featureIds,
        extras,
        `${window.location.origin}/apps/${appId}`
      );
    } catch (e) {
      console.error('could not open the Stripe checkout', e);
      // Paddle reports what goes wrong inside its frame as
      // containers_checkout_event; the Stripe session failing to open is the
      // one failure this side can see before the buyer leaves.
      trackEvent('containers_checkout_event', {
        ...tags,
        name: 'session_create_failed',
        error_detail: e instanceof Error ? e.message.slice(0, 200) : undefined,
      });

      return {
        ok: false,
        reason: 'FAILED',
        message: e instanceof Error && e.message ? e.message : "We couldn't reach the payment service.",
      };
    }

    trackEvent('containers_checkout_opened', tags);
    // The tags ride along to the page Stripe sends the buyer back to, which
    // reports how the checkout ended.
    rememberStripeCheckout(session.sessionId, tags);
    window.location.assign(session.url);

    return await new Promise<PurchaseOutcome>(() => undefined);
  };

  const total =
    (summary?.planPrice || 0) +
    (summary?.lines || []).reduce((sum, line) => sum + line.price, 0);

  const lines = (
    <>
      {summary?.features?.length ? (
        <ul className="list-none m-0 mb-5 p-0 flex flex-col gap-2.5">
          {summary.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2.5 text-ds-125 leading-[1.4] text-navy-100">
              <CheckIcon size={14} className="flex-none mt-0.5 text-state-ok" />
              <span className="min-w-0">{feature}</span>
            </li>
          ))}
        </ul>
      ) : null}
      <ul className="list-none m-0 p-0 border-t border-navy-600">
        <li className="flex items-baseline justify-between gap-4 py-3 border-b border-navy-600">
          {/* The size sits in the plan's own row, not under the heading: with
              a database on the next line, "512 MB" beside the title could be
              either one's. Here it is plainly the container's. */}
          <span className="min-w-0 flex flex-col gap-1">
            <span className="text-ds-135 text-navy-100">{summary?.planName}</span>
            {summary?.planSpec ? (
              <span className="font-courrier text-ds-115 text-navy-300">{summary.planSpec}</span>
            ) : null}
          </span>
          <span className="flex-none font-courrier text-ds-115 text-navy-200">
            {formatMoney(summary?.planPrice || 0)}/mo
          </span>
        </li>
        {(summary?.lines || []).map((line) => (
          <li key={line.id} className="flex items-baseline justify-between gap-4 py-3 border-b border-navy-600">
            <span className="min-w-0 text-ds-135 text-navy-100">
              {line.name}
              {line.detail ? (
                <span className="ml-2 font-courrier text-ds-105 text-navy-300">{line.detail}</span>
              ) : null}
            </span>
            <span className="flex-none font-courrier text-ds-115 text-navy-200">
              {formatMoney(line.price)}/mo
            </span>
          </li>
        ))}
      </ul>

      <div className="flex items-baseline justify-between gap-4 pt-4">
        <span className="text-ds-135 text-white font-medium">Total today</span>
        <span className="font-courrier text-ds-15 text-white">{formatMoney(total)}</span>
      </div>

      {summary?.note ? (
        <p className="m-0 mt-4 text-ds-115 leading-[1.5] text-navy-300">{summary.note}</p>
      ) : null}
    </>
  );

  const checkoutModal = !isCheckoutOpen ? null : (
    <>
      <div
        className={`fixed inset-0 z-[10000] ${
          options.plainOverlay ? 'bg-black/70' : 'bg-black/70 backdrop-blur-sm'
        }`}
      />
      <div className="fixed inset-0 z-[10001] flex items-start justify-center overflow-y-auto p-2 md:p-4 lg:p-8">
        <div className="relative w-full max-w-[980px] my-auto bg-navy-800 border border-navy-400 rounded-card shadow-[0_14px_48px_rgba(0,0,0,0.5)] overflow-hidden grid grid-cols-1 md:grid-cols-2">
          {/* On the card, not in a column: on a phone the columns stack and
              the Paddle column's corner is halfway down the page. */}
          <button
            type="button"
            onClick={() => {
              // Both, in this order: tear down Paddle's frame, then settle
              // the wait ourselves — inline, `checkout.closed` does not come.
              closeCheckout();
              settle({ ok: false, reason: 'CLOSED' });
            }}
            aria-label="Close checkout"
            className="b4-focus absolute top-3 right-3 z-10 h-8 w-8 rounded-control inline-flex items-center justify-center text-navy-200 hover:text-white hover:bg-navy-600 transition-colors duration-[120ms]"
          >
            ✕
          </button>

          {/* Ours: what is being bought, item by item. Paddle's inline frame
              renders only the payment form — the summary is our job, and an
              overlay checkout would bring its own and duplicate it.

              On a phone it is a head over the form rather than a column
              beside it: the plan, the price, and the lines behind "Show
              details" — the card form is what the screen is for, and a
              full itemisation above it pushed the form below the fold. */}
          <div className="min-w-0 px-4 pt-5 pb-4 md:p-6 lg:p-8 bg-navy-900 border-b md:border-b-0 md:border-r border-navy-600 flex flex-col">
            <div className="md:hidden flex flex-col items-center text-center">
              <span className="text-ds-135 text-navy-200">{summary?.planName}</span>
              <div className="mt-1 flex items-baseline gap-1.5">
                <span className="font-sora text-[32px] leading-none text-white">{formatMoney(total)}</span>
                <span className="text-ds-115 text-navy-300">/month</span>
              </div>
              <p className="m-0 mt-2 text-ds-115 leading-[1.5] text-navy-300">
                Billed monthly · cancel any time
              </p>
              <button
                type="button"
                onClick={() => setIsDetailsOpen((open) => !open)}
                aria-expanded={isDetailsOpen}
                className="b4-focus mt-3 inline-flex items-center gap-1.5 h-9 px-4 rounded-control bg-navy-700 text-ds-125 font-medium text-white hover:bg-navy-600 transition-colors duration-[120ms]"
              >
                {isDetailsOpen ? 'Hide details' : 'Show details'}
                <ChevronDownIcon
                  size={14}
                  className={`transition-transform duration-[120ms] ${isDetailsOpen ? 'rotate-180' : ''}`}
                />
              </button>
            </div>

            <div className="hidden md:block">
              <Eyebrow>{summary?.eyebrow || (summary ? `pay for ${summary.planName}` : 'payment')}</Eyebrow>
              <h2 className="m-0 mt-2 font-sora text-[28px] leading-[1.15] text-white">
                {summary?.planName}
              </h2>

              <div className="mt-5 flex items-baseline gap-1.5">
                <span className="font-sora text-[32px] leading-none text-white">{formatMoney(total)}</span>
                <span className="text-ds-115 text-navy-300">/month</span>
              </div>
              <p className="m-0 mt-2 text-ds-115 leading-[1.5] text-navy-300">
                Billed monthly · cancel any time
              </p>
            </div>

            <div className={`${isDetailsOpen ? 'block' : 'hidden'} md:block mt-4 md:mt-6`}>
              {lines}
            </div>

            <div className="hidden md:block mt-auto pt-8">
              <div className="font-courrier text-[length:calc(11px*var(--b4-type,1))] tracking-[0.08em] uppercase text-navy-400">
                powered by
              </div>
              <div className="mt-1 font-sora text-ds-135 text-navy-200">Paddle</div>
              <div className="mt-1 font-courrier text-[length:calc(11px*var(--b4-type,1))] tracking-[0.08em] uppercase text-navy-400">
                merchant of record · pci dss level 1
              </div>
            </div>
          </div>

          {/* Paddle's */}
          <div className="min-w-0 relative p-4 lg:p-6">
            <div className="checkout-container w-full min-h-[450px]" />
          </div>
        </div>
      </div>
    </>
  );

  return { configured, purchase, checkoutModal };
}
